/**
 * Landing page (T046).
 *
 * Lets a visitor enter a display name, then either:
 *   - Create a new room → POST /api/rooms → navigate to /room/:id
 *   - Join an existing room by pasting a link or ID → navigate to /room/:id
 *
 * Name is persisted to localStorage so subsequent visits prefill it (T054).
 * UI is styled with CSS Modules; form controls are plain HTML — Radix
 * primitives are reserved for cases that need accessibility heavy lifting
 * (dialogs, toasts).
 */
import { useEffect, useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import * as Label from '@radix-ui/react-label'
import { createRoom } from '../lib/api.js'
import { readDisplayName, writeDisplayName } from '../lib/displayName.js'
import styles from './Landing.module.css'

export function Landing() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [joinValue, setJoinValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setName(readDisplayName())
  }, [])

  const createMutation = useMutation({
    mutationFn: createRoom,
    onMutate: () => setError(null),
    onSuccess: ({ roomId }) => {
      writeDisplayName(name.trim())
      navigate(`/room/${roomId}`)
    },
    onError: () => setError(t('landing.createError')),
  })

  function onCreate(e: FormEvent) {
    e.preventDefault()
    if (!name.trim()) return
    createMutation.mutate()
  }

  function onJoin(e: FormEvent) {
    e.preventDefault()
    if (!name.trim() || !joinValue.trim()) return
    const id = extractRoomId(joinValue.trim())
    if (!id) {
      setError(t('landing.joinError'))
      return
    }
    writeDisplayName(name.trim())
    navigate(`/room/${id}`)
  }

  const canSubmit = name.trim().length > 0 && !createMutation.isPending

  return (
    <main className={styles.shell}>
      <section className={styles.card}>
        <h1 className={styles.headline}>{t('landing.headline')}</h1>
        <p className={styles.tagline}>{t('landing.tagline')}</p>

        <form onSubmit={onCreate}>
          <div className={styles.field}>
            <Label.Root htmlFor="name" className={styles.label}>
              {t('landing.yourName')}
            </Label.Root>
            <input
              id="name"
              className={styles.input}
              placeholder={t('landing.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              maxLength={80}
            />
          </div>

          <button className={styles.button} type="submit" disabled={!canSubmit}>
            {t('landing.createRoom')}
          </button>
        </form>

        <div className={styles.divider}>{t('landing.joinSection')}</div>

        <form onSubmit={onJoin}>
          <div className={styles.joinRow}>
            <input
              className={styles.input}
              placeholder={t('landing.joinLinkPlaceholder')}
              value={joinValue}
              onChange={(e) => setJoinValue(e.target.value)}
            />
            <button
              className={styles.button}
              type="submit"
              disabled={!name.trim() || !joinValue.trim()}
            >
              {t('landing.joinButton')}
            </button>
          </div>
        </form>

        {error && <div className={styles.error}>{error}</div>}
      </section>
    </main>
  )
}

/**
 * Accept either a full URL like `https://host/room/abc12345`, a path like
 * `/room/abc12345`, or a bare room id. Returns the id or null.
 */
function extractRoomId(input: string): string | null {
  // Bare id: hex/alphanumeric, 8–64 chars.
  if (/^[A-Za-z0-9_-]{8,64}$/.test(input)) return input

  try {
    const url = input.startsWith('http') ? new URL(input) : new URL(input, 'http://x')
    const m = url.pathname.match(/\/room\/([A-Za-z0-9_-]{8,64})\/?$/)
    return m?.[1] ?? null
  } catch {
    return null
  }
}
